import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Plus, TrendingUp, Edit as EditIcon, Calendar } from 'lucide-react';
import { theme } from '../../../theme';
import { useComplexDetail } from '../../../hooks/venue/useComplexDetail';

// Removed local interfaces - using VenueComplex from types/venue.ts

const ComplexDetailPageEnhanced: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { complex, loading, error, fetchComplex } = useComplexDetail(id || '');

  useEffect(() => {
    if (id) {
      fetchComplex();
    }
  }, [id, fetchComplex]);

  if (loading || !complex) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        {loading ? 'Cargando complejo...' : 'Complejo no encontrado'}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center'
      }}>
        <div style={{ color: theme.colors.danger, marginBottom: '16px' }}>
          {error}
        </div>
        <button
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            backgroundColor: theme.colors.primary,
            border: 'none',
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#000',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/owner/venues')}
        >
          Volver a Mis Canchas
        </button>
      </div>
    );
  }

  // Styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
    paddingBottom: '48px',
  };

  const headerStyle: React.CSSProperties = {
    padding: '32px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const breadcrumbRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const breadcrumbsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  };

  const breadcrumbStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: isActive ? theme.colors.text : theme.colors.textSecondary,
    cursor: isActive ? 'default' : 'pointer',
  });

  const headerActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const secondaryBtnStyle: React.CSSProperties = {
    padding: '14px 32px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: `2px solid ${theme.colors.primary}`,
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: '600',
    color: theme.colors.primary,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const primaryBtnStyle: React.CSSProperties = {
    padding: '16px 32px',
    borderRadius: '12px',
    backgroundColor: theme.colors.primary,
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#000',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  const titleSectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '48px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '2px',
  };

  const addressRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const addressTextStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: theme.colors.textSecondary,
  };

  const contentAreaStyle: React.CSSProperties = {
    display: 'flex',
    gap: '32px',
    padding: '0 48px',
  };

  const leftColStyle: React.CSSProperties = {
    width: '820px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const rightColStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '18px',
    border: `1px solid ${theme.colors.border}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '20px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '1px',
  };

  const cardSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const mainPhotoStyle: React.CSSProperties = {
    width: '100%',
    height: '320px',
    borderRadius: '12px',
    backgroundColor: '#1A1A1A',
  };

  const thumbnailsStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
  };

  const thumbnailStyle: React.CSSProperties = {
    height: '80px',
    borderRadius: '8px',
    backgroundColor: '#2A2A2A',
  };

  const descStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: theme.colors.textSecondary,
    lineHeight: 1.6,
  };

  const amenitiesRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const amenityStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const amenityTextStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const statsRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  };

  const statBoxStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const statLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const statValueStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '28px',
    color: theme.colors.text,
  };

  const venueItemStyle = (isActive: boolean): React.CSSProperties => ({
    backgroundColor: theme.colors.background,
    borderRadius: '12px',
    border: `${isActive ? '2px' : '1px'} solid ${isActive ? theme.colors.primary : theme.colors.border}`,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  });

  const venueHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const venueNameStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '15px',
    fontWeight: '600',
    color: theme.colors.text,
    margin: 0,
  };

  const venueDetailsStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const badgeStyle = (status: string): React.CSSProperties => ({
    backgroundColor: status === 'ACTIVE' ? theme.colors.success : '#33383B',
    color: status === 'ACTIVE' ? '#000' : theme.colors.textSecondary,
    borderRadius: '20px',
    padding: '6px 16px',
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: '600',
  });

  const venueActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const actionBtnStyle = (isPrimary: boolean): React.CSSProperties => ({
    flex: 1,
    height: '32px',
    borderRadius: '8px',
    backgroundColor: isPrimary ? theme.colors.primary : 'transparent',
    border: `1px solid ${isPrimary ? theme.colors.primary : theme.colors.border}`,
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    fontWeight: '600',
    color: isPrimary ? '#000' : theme.colors.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  });

  const addVenueStyle: React.CSSProperties = {
    height: '56px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: `2px dashed ${theme.colors.primary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const addVenueTextStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: '600',
    color: theme.colors.primary,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={breadcrumbRowStyle}>
          <div style={breadcrumbsStyle}>
            <span style={breadcrumbStyle(false)} onClick={() => navigate('/owner/complex')}>
              Mis Complejos
            </span>
            <span style={{ color: '#6C757D' }}>/</span>
            <span style={breadcrumbStyle(true)}>{complex.name}</span>
          </div>
          <div style={headerActionsStyle}>
            <button style={secondaryBtnStyle}>Editar Complejo</button>
            <button style={primaryBtnStyle}>Agregar Cancha</button>
          </div>
        </div>
        <div style={titleSectionStyle}>
          <h1 style={titleStyle}>{complex.name}</h1>
          <div style={addressRowStyle}>
            <MapPin size={18} color={theme.colors.textSecondary} />
            <span style={addressTextStyle}>
              {complex.address}, {complex.city}, {complex.province}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={contentAreaStyle}>
        {/* Left Column */}
        <div style={leftColStyle}>
          {/* Photos Card */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <h3 style={cardTitleStyle}>FOTOS</h3>
              <span style={cardSubtitleStyle}>{complex.photos?.length || 0} fotos</span>
            </div>
            <div style={mainPhotoStyle} />
            <div style={thumbnailsStyle}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={thumbnailStyle} />
              ))}
            </div>
          </div>

          {/* Info Card */}
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>INFORMACIÓN</h3>
            <p style={descStyle}>{complex.description}</p>
            <div style={amenitiesRowStyle}>
              {complex.amenities.map((amenity, i) => (
                <div key={i} style={amenityStyle}>
                  <span style={{ fontSize: '16px' }}>
                    {i === 0 ? '🅿️' : i === 1 ? '🍔' : '🚿'}
                  </span>
                  <span style={amenityTextStyle}>{amenity}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Card */}
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>STATS AGREGADAS</h3>
            <div style={statsRowStyle}>
              <div style={statBoxStyle}>
                <span style={statLabelStyle}>Total Reservas</span>
                <span style={statValueStyle}>{((complex as any).stats?.totalBookings || 0).toLocaleString()}</span>
              </div>
              <div style={statBoxStyle}>
                <span style={statLabelStyle}>Revenue Total</span>
                <span style={statValueStyle}>${((complex as any).stats?.totalRevenue || 0).toLocaleString()}</span>
              </div>
              <div style={statBoxStyle}>
                <span style={statLabelStyle}>Rating Promedio</span>
                <span style={statValueStyle}>{((complex as any).stats?.averageRating || 0).toFixed(1)} ⭐</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={rightColStyle}>
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <h3 style={cardTitleStyle}>CANCHAS DEL COMPLEJO</h3>
              <span style={cardSubtitleStyle}>{complex.venues?.length || 0} canchas</span>
            </div>

            {/* Venues List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {complex.venues?.map((venue) => (
                <div key={venue.id} style={venueItemStyle(venue.status === 'ACTIVE')}>
                  <div style={venueHeaderStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <h4 style={venueNameStyle}>{venue.name}</h4>
                      <p style={venueDetailsStyle}>{venue.venueType}</p>
                    </div>
                    <span style={badgeStyle(venue.status)}>
                      {venue.status === 'ACTIVE' ? 'Activa' : 'Pausada'}
                    </span>
                  </div>
                  <div style={venueActionsStyle}>
                    <button style={actionBtnStyle(false)}>
                      <TrendingUp size={14} />
                      Ver Stats
                    </button>
                    <button style={actionBtnStyle(false)}>
                      <EditIcon size={14} />
                      Editar
                    </button>
                    <button style={actionBtnStyle(true)}>
                      <Calendar size={14} />
                      Config
                    </button>
                  </div>
                </div>
              ))}

              {/* Add New Venue */}
              <div style={addVenueStyle}>
                <Plus size={20} color={theme.colors.primary} />
                <span style={addVenueTextStyle}>Agregar Nueva Cancha</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplexDetailPageEnhanced;
